import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { SprayCan } from "lucide-react";
import { z } from "zod";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuthStore";
import { savePendingAuthorization } from "../lib/oauthPending";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Supabase Auth OAuth 2.1 server responses. Called over REST because the installed
// supabase-js predates `supabase.auth.oauth.*`; the endpoints are the ones it wraps.
const ZAuthorizationDetails = z.object({
  authorization_id: z.string(),
  redirect_uri: z.string(),
  scope: z.string(),
  client: z.object({
    id: z.string(),
    name: z.string(),
    uri: z.string().nullable().optional(),
    logo_uri: z.string().nullable().optional(),
  }),
});
type IAuthorizationDetails = z.infer<typeof ZAuthorizationDetails>;

const ZRedirect = z.object({ redirect_url: z.string().url() });
const ZAuthError = z.object({ msg: z.string().optional(), message: z.string().optional(), error_description: z.string().optional() });

const SCOPE_LABELS: Record<string, string> = {
  openid: "Confirm who you are",
  email: "See your email address",
  profile: "See your name and profile photo",
  phone: "See your phone number",
};

async function authRequest(
  path: string,
  init: { method?: string; body?: string } = {}
): Promise<unknown> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");
  const response = await fetch(`${supabaseUrl}/auth/v1${path}`, {
    method: init.method ?? "GET",
    body: init.body,
    headers: {
      apikey: supabasePublishableKey,
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = ZAuthError.safeParse(body);
    const message = parsed.success
      ? parsed.data.msg ?? parsed.data.message ?? parsed.data.error_description
      : undefined;
    throw new Error(message ?? `Request failed (${response.status})`);
  }
  return body;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Only follow redirects to http(s) URLs (never javascript: etc). */
function safeRedirect(url: string) {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error("Refusing to redirect to an unsupported URL");
  }
  window.location.assign(parsed.toString());
}

/** True when rendered inside a frame — consent must never be clickjacked. */
function isFramed(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

export default function OAuthConsentPage() {
  const history = useHistory();
  const location = useLocation();
  const { user, initialized } = useAuth();
  const authorizationId = new URLSearchParams(location.search).get("authorization_id");

  const [details, setDetails] = useState<IAuthorizationDetails | null>(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadDetails = useCallback(async (id: string) => {
    try {
      const body = await authRequest(`/oauth/authorizations/${encodeURIComponent(id)}`);
      const needsConsent = ZAuthorizationDetails.safeParse(body);
      if (needsConsent.success) {
        setDetails(needsConsent.data);
        return;
      }
      // The user already approved this client for these scopes: go straight back.
      safeRedirect(ZRedirect.parse(body).redirect_url);
    } catch (err) {
      console.error("Error loading authorization request:", err);
      setError(err instanceof Error ? err.message : "Could not load this authorization request.");
    }
  }, []);

  useEffect(() => {
    if (!authorizationId || !initialized) return;
    if (!user) {
      savePendingAuthorization(authorizationId);
      history.push("/login");
      return;
    }
    loadDetails(authorizationId);
  }, [authorizationId, user, initialized, history, loadDetails]);

  const decide = async (action: "approve" | "deny") => {
    if (!authorizationId) return;
    setIsSubmitting(true);
    try {
      const body = await authRequest(`/oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      safeRedirect(ZRedirect.parse(body).redirect_url);
    } catch (err) {
      console.error(`Error sending ${action}:`, err);
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setIsSubmitting(false);
    }
  };

  if (isFramed()) {
    return (
      <IonPage>
        <IonContent>
          <p className="p-6 text-red-400 text-sm">
            For your security, this page can't be shown inside another site. Open it directly in your browser.
          </p>
        </IonContent>
      </IonPage>
    );
  }

  const scopes = details ? details.scope.split(" ").filter(Boolean) : [];

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="max-w-sm w-full space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <SprayCan className="w-8 h-8 text-primary" />
              </div>
              {details ? (
                <>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Connect {details.client.name} to Tidy?
                  </h1>
                  <p className="text-muted-foreground/70 text-sm mb-3">
                    {details.client.name} will be able to act as you through Tidy's assistant connection: see
                    everything in your households (members, areas, tasks and history) and, depending on your role,
                    create and edit areas and tasks, mark tasks complete, and delete them (parents only).
                  </p>
                  <p className="text-foreground text-sm">
                    Access goes to <strong data-testid="oauth-redirect-host">{hostOf(details.redirect_uri)}</strong>.
                    Only allow this if you just started connecting an assistant yourself and recognise that site.
                  </p>
                </>
              ) : (
                <h1 className="text-2xl font-bold text-foreground">Authorize app</h1>
              )}
            </div>

            {!authorizationId && (
              <p className="text-red-400 text-sm text-center">This link is missing its authorization request.</p>
            )}
            {error && <p className="text-red-400 text-sm text-center" role="alert">{error}</p>}

            {details && (
              <>
                <div className="bg-surface rounded-xl p-4 border border-border space-y-2 text-sm">
                  {scopes.length > 0 && (
                    <ul className="list-disc list-inside text-foreground space-y-1">
                      {scopes.map((s) => (
                        <li key={s}>{SCOPE_LABELS[s] ?? s}</li>
                      ))}
                    </ul>
                  )}
                  {user?.email && (
                    <p className="text-muted-foreground/70">Signed in as {user.email}</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => decide("deny")}
                    disabled={isSubmitting}
                    className="flex-1 bg-surface-raised hover:bg-surface text-foreground px-4 py-3 rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    Deny
                  </button>
                  <button
                    onClick={() => decide("approve")}
                    disabled={isSubmitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    {isSubmitting ? "Working..." : "Allow"}
                  </button>
                </div>
                <p className="text-muted-foreground/70 text-xs text-center">
                  To disconnect later, remove Tidy from the assistant's connector settings.
                </p>
              </>
            )}

            {!details && !error && authorizationId && (
              <p className="text-muted-foreground/70 text-sm text-center">Loading…</p>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
